<!--

The main grid contains all the major components of the Glim application in a grid
(of sorts). The main goal here is to make sure all important data is at the finger-
tips of the end-user.

Note that this view should never have a scroll-bar. Individual components can have
them, but any element in this view should aways be visible.

-->

<template>
  <div class="component-grid">
    <FileToolbar
      v-if    = "ui.layout.fileToolbar.visible"
    >
    </FileToolbar>

    <VerticalSplitPane 
      class       = "panes py-6"
      :split      = "ui.columnsSplitPercent"
      :hideRight  = "hideRightColumn"
      @resize     = "onResizedColumns"
    >

      <template v-slot:left>

        <HorizontalSplitPane 
          class       = "left column mr-6"
          :split      = "ui.mediaColumnSplitPercent"
          :hideTop    = "!ui.layout.videoPlayer.visible"
          :hideBottom = "!ui.layout.timeline.visible"
          @resize     = "onResizeMediaColumn"
        >

          <template v-slot:top>
            <VideoPane class="top mb-6" />
          </template>

          <template v-slot:bottom>
            <TimelinePane class="bottom" />
          </template>

        </HorizontalSplitPane>

      </template>

      <template v-slot:right>

        <HorizontalSplitPane 
          class       = "right column"
          :split      = "ui.infoColumnSplitPercent"
          :hideTop    = "!ui.layout.topTabs.visible"
          :hideBottom = "!ui.layout.bottomTabs.visible"
          @resize     = "onResizeInfoColumn"
        >

          <template v-slot:top>
            <TabbedPane 
              class     = "top" 
              :tabs     = "ui.layout.topTabs.tabs"
              :exclude  = "ui.layout.bottomTabs.tabs"
              :hidden   = "hiddenTabs"
              :locked   = "ui.layout.topTabs.locked"
              @tabs     = "onTopTabsChanged"
              @reset    = "onResetTabs"
            />
          </template>

          <template v-slot:bottom>
            <TabbedPane 
              class     = "bottom" 
              :tabs     = "ui.layout.bottomTabs.tabs"
              :exclude  = "ui.layout.topTabs.tabs"
              :hidden   = "hiddenTabs"
              :locked   = "ui.layout.bottomTabs.locked"
              @tabs     = "onBottomTabsChanged"
              @reset    = "onResetTabs"
            />
          </template>

        </HorizontalSplitPane>

      </template>

    </VerticalSplitPane>

    <SnacksPane></SnacksPane>
    <ClipListLinkHelper></ClipListLinkHelper>
  </div>
</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import ClipListLinkHelper from '../components/panes/components/cliplist/ClipListLinkHelper.vue';
import FileToolbar from '../components/file-toolbar/FileToolbar';
import HorizontalSplitPane from '../components/panes/HorizontalSplitPane';
import SnacksPane from '../components/panes/components/SnacksPane';
import TabbedPane from '../components/panes/TabbedPane';
import TimelinePane from '../components/panes/TimelinePane';
import VerticalSplitPane from '../components/panes/VerticalSplitPane';
import VideoPane from '../components/panes/VideoPane';
import { 
  HANDLE_LINKED_MEDIA,
  RESET_TABS,
  RESIZE_COLUMNS,
  RESIZE_MEDIA_COLUMN,
  RESIZE_INFO_COLUMN,
  SELECT_TOP_VISIBLE_TABS,
  SELECT_BOTTOM_VISIBLE_TABS
} from '@/utils/action-types';

// #endregion Imports

export default {

  // #region Properties

  name:       'GridView',
  components: {
    ClipListLinkHelper,
    FileToolbar,
    HorizontalSplitPane,
    SnacksPane,
    TabbedPane,
    TimelinePane,
    VerticalSplitPane,
    VideoPane
  },
  computed: {
    ...mapState([
      'ui',
      'experimentalFeatures'
    ]),
    hideRightColumn() {
      return !this.ui.layout.topTabs.visible && !this.ui.layout.bottomTabs.visible;
    },
    hiddenTabs() {
      const hidden = [];

      if (this.experimentalFeatures && !this.experimentalFeatures.scopes) {
        hidden.push('Scopes');
      }

      if (this.experimentalFeatures && !this.experimentalFeatures.annotations) {
        hidden.push('Annotations');
      }

      return hidden;
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.$store.dispatch(HANDLE_LINKED_MEDIA);
  },

  // #endregion Life-Cycle

  // #region Methods

  methods: {

    // #region Events

    onResizedColumns(percentage) {
      this.$store.dispatch(RESIZE_COLUMNS, percentage);
    },

    onResizeMediaColumn(percentage) {
      this.$store.dispatch(RESIZE_MEDIA_COLUMN, percentage);
    },

    onResizeInfoColumn(percentage) {
      this.$store.dispatch(RESIZE_INFO_COLUMN, percentage);
    },

    onTopTabsChanged(tabs) {
      this.$store.dispatch(SELECT_TOP_VISIBLE_TABS, tabs);
    },

    onBottomTabsChanged(tabs) {
      this.$store.dispatch(SELECT_BOTTOM_VISIBLE_TABS, tabs);
    },

    onResetTabs() {
      this.$store.dispatch(RESET_TABS);
    }

    // #endregion Events

  }

  // #endregion Methods

}
</script>

<style scoped lang="scss">

.component-grid {
  display:        flex;
  flex-direction: column;
  width:          100%;
  height:         100%;

  .panes {
    flex: 1;

    .left,
    .right,
    .top,
    .bottom {
      height:     100%;
      width:      100%;
    }
  }
}

</style>
